<script setup lang="ts">
/**
 * The repos this box hosts, each with the commands it is built, committed and
 * deployed with, and the domains it is served on. Admins only, beside tmux.
 *
 * A command runs in a tmux session of its own in the claude user's checkout
 * of the repo, and the view hands over to tmux on it: a build's output is a
 * terminal's, and a deploy that asks a question needs somebody to answer it.
 * A command that reads `$MESSAGE` (Commit, as it comes) asks for one first.
 *
 * The list comes from the bridge, not the server — todos cannot see /home —
 * so "the tmux bridge is not running" is the usual reason it is empty.
 *
 * With `only`, it is that one repo's card and nothing else, open for editing
 * from the start and for as long as it is shown: the git panel's settings
 * modal (RepoSettingsModal.vue) is this view cut down to its repo. Save and
 * Cancel there are done with it, so they close the modal.
 *
 * Its prompts are what Claude is told in this repo instead of the server-wide
 * ones (`REPO_PROMPTS`): saved with the commands, blank for the default. So are
 * its worktree hooks (`WORKTREE_HOOKS`), blank for none, and the test command
 * a branch's auto-test runs, blank for `npm test`.
 *
 * Under the boxes, its branches and the merges between them (RepoBranches.vue):
 * where worktrees go, what merges into what by hand or on its own, what is
 * pushed, deployed and tested. Saved as they change, like the boxes.
 * Under those, its client feedback keys (FeedbackKeys.vue), made here so the
 * repo is set by the card they are made on; live too.
 *
 * Beside each name, the output tokens Claude has spent in the repo, rolled up
 * over its checkout and every worktree it has had (`RepoInfo.claudeTokens`).
 */
import { computed, onMounted, reactive, ref, watch } from "vue";
import { formatTokens } from "../lib/format.js";
import { toast } from "../lib/toast.js";
import { useViewport } from "../stores/viewport.js";
import { useBoard } from "../stores/board.js";
import {
  createRepo,
  repoOnBoard,
  loadRepoInfos as load,
  repoInfos as repos,
  repoInfosError as listError,
  repoInfosLoaded as loaded,
  runRepoCommand,
  saveRepoSettings,
  setAutoCommit,
  setAutoFix,
  setMaxClaudes,
  setStartLimit,
} from "../lib/repoSettings.js";
import RepoBranches from "./RepoBranches.vue";
import FeedbackKeys from "./FeedbackKeys.vue";
import StartModeSelect from "./StartModeSelect.vue";
import { START_LIMIT_TITLES } from "../lib/startMode.js";
import type { StartMode } from "@shared/startMode.js";
import { deployStatusChanged, deployStatuses, loadDeployStatuses } from "../lib/deploys.js";
import { loadTaskPromptTemplate, taskPromptTemplate } from "../lib/taskPromptTemplate.js";
import { REPO_NAME } from "@shared/tmux.js";
import {
  DEFAULT_AUTO_FIX_PROMPT,
  AUTO_MERGE_CONFLICT_PROMPT,
  REPO_PROMPTS,
  REPO_PROMPT_MAX,
  WORKTREE_HOOKS,
  DEFAULT_TEST_COMMAND,
  type RepoPromptKey,
  type WorktreeHookKey,
  REPO_COMMAND_LABEL_MAX,
  REPO_COMMAND_MAX,
  RUN_MESSAGE_MAX,
  autoFixModeOf,
  startLimitOf,
  maxClaudesOf,
  isRepoMaxClaudes,
  REPO_MAX_CLAUDES_MAX,
  needsMessage,
  newCommandId,
  normalizeDomain,
  type RepoCommand,
  type RepoInfo,
  type RepoSettings,
} from "@shared/repoSettings.js";

const props = defineProps<{ only?: string }>();
/** `ran`: a command was run, and the view has handed over to its tmux session.
 *  `close`: with `only`, the edit was saved or cancelled. */
const emit = defineEmits<{ ran: []; close: [] }>();

const viewport = useViewport();
const board = useBoard();
const query = ref("");

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err));

onMounted(() => {
  void load();
  void loadDeployStatuses();
});

const shown = computed(() => {
  if (props.only) return repos.value.filter((r) => r.name === props.only);
  // Only this board's: its team's git folders (shared/repoFolders.ts).
  const here = repos.value.filter((r) => repoOnBoard(r, board.teamId));
  const q = query.value.trim().toLowerCase();
  return q ? here.filter((r) => r.name.toLowerCase().includes(q)) : here;
});

/** Where this board's repos come from, for when there are none. */
const teamFolders = computed((): string => {
  const listed = board.team?.repoFolders ?? [];
  return listed.length ? listed.map((f) => `/home/git/${f}`).join(", ") : "/home/git/repos";
});

const href = (domain: string): string => `https://${domain}`;
/** The message box takes the keyboard as it appears. */
const vFocus = { mounted: (el: HTMLElement) => el.focus() };

// ── a new repo ───────────────────────────────────────────────────────────
/** The name being typed, while the New repo form is open; null when it is shut. */
const newName = ref<string | null>(null);
const creating = ref(false);
const newNameOk = computed(() => REPO_NAME.test((newName.value ?? "").trim().replace(/\.git$/, "")));

async function create(): Promise<void> {
  if (!newNameOk.value || creating.value) return;
  const name = newName.value!.trim().replace(/\.git$/, "");
  creating.value = true;
  try {
    await createRepo(name, board.teamId);
    newName.value = null;
    query.value = "";
    toast(`Made ${name}`);
  } catch (err) {
    toast(message(err));
  } finally {
    creating.value = false;
  }
}

// ── the auto boxes ───────────────────────────────────────────────────────
/** Every automatic move a repo makes, each its own box, each its own flag
 *  route. The git panel's 🛠 opens this card, and its ⚙ options leave these out. The
 *  merges, pushes, deploys, tests and "start at review" are each branch's (RepoBranches.vue), and
 *  auto-fix is a start mode, below them. */
type AutoKey = "autoCommit";
/** `on` / `off`: what the repo does with the box as it stands, shown under it.
 *  `get`: whether it is ticked, where absent is not simply off. */
const AUTOS: { key: AutoKey; label: string; on: string; off: string; get?: (s: RepoSettings) => boolean; set: (repo: string, on: boolean) => Promise<void> }[] = [
  {
    key: "autoCommit",
    label: "Auto commit",
    on: "When a Claude in a worktree stops, its changes are committed under its task's title.",
    off: "A worktree's changes stay uncommitted until someone commits them.",
    set: setAutoCommit,
  },
];

function autoOn(repo: RepoInfo, auto: (typeof AUTOS)[number]): boolean {
  return auto.get ? auto.get(repo.settings) : !!repo.settings[auto.key];
}

/** How auto-fix starts its Claudes (Manual is off): from the server's status where there is one. */
const fixMode = (repo: RepoInfo): StartMode => deployStatuses.value.get(repo.name)?.autoFix ?? autoFixModeOf(repo.settings);
const fixOn = (repo: RepoInfo): boolean => fixMode(repo) !== "manual";
const FIX_ON = "A failed Deploy starts a Claude in a new worktree to fix it, and only that fix is merged meanwhile. A merge that conflicts always gets a Claude too: the worktree's own, resumed if it has gone, or one off the branch merged into.";
const FIX_OFF = "A failed Deploy only turns auto-deploy off and says where it failed, for someone to fix; a merge that conflicts is only told to a Claude still in its worktree.";

/** Auto-fix's box, or the start limit: either changes how auto-fix starts. */
async function changeFix(repo: RepoInfo, key: "autoFix" | "startLimit", save: () => Promise<void>, ev?: Event): Promise<void> {
  autoSaving.value = `${repo.name}/${key}`;
  try {
    await save();
    const st = deployStatuses.value.get(repo.name);
    if (st) deployStatusChanged({ ...st, autoFix: autoFixModeOf(repos.value.find((r) => r.name === repo.name)?.settings) });
  } catch (err) {
    if (ev) (ev.target as HTMLInputElement).checked = !(ev.target as HTMLInputElement).checked;
    toast(message(err));
  } finally {
    autoSaving.value = null;
  }
}
const LIMIT_OFF = "Each queued task and prompt goes by its own start mode.";
const limitBlurb = (mode: StartMode): string =>
  mode === "ready" ? LIMIT_OFF : `Every queued task and prompt here, and auto-fix, goes no sooner than ${mode === "under" ? "Under" : "Wastage"} would, whatever its own start mode.`;

const autoSaving = ref<string | null>(null);

/** Its "Max Claudes", saved as it is left or Enter is pressed: blank is none. */
async function changeMaxClaudes(repo: RepoInfo, ev: Event): Promise<void> {
  const box = ev.target as HTMLInputElement;
  const was = maxClaudesOf(repo.settings);
  const typed = box.value.trim();
  const max = typed === "" ? null : Number(typed);
  if (max === was) return;
  if (max !== null && !isRepoMaxClaudes(max)) {
    box.value = was === null ? "" : String(was);
    toast(`Max Claudes is a whole number from 1 to ${REPO_MAX_CLAUDES_MAX}, or blank for no limit`);
    return;
  }
  autoSaving.value = `${repo.name}/maxClaudes`;
  try {
    await setMaxClaudes(repo.name, max);
  } catch (err) {
    box.value = was === null ? "" : String(was);
    toast(message(err));
  } finally {
    autoSaving.value = null;
  }
}
const maxClaudesBlurb = (max: number | null): string =>
  max === null
    ? "Only the global settings' Parallel Claudes limits how many Claudes work here at once."
    : `At most ${max} Claude${max === 1 ? "" : "s"} at work here at once; another start waits, queued, until one stops. Auto-fix is not held.`;
async function toggleAuto(repo: RepoInfo, auto: (typeof AUTOS)[number], ev: Event): Promise<void> {
  const box = ev.target as HTMLInputElement;
  const on = box.checked;
  autoSaving.value = `${repo.name}/${auto.key}`;
  try {
    await auto.set(repo.name, on);
  } catch (err) {
    box.checked = !on;
    toast(message(err));
  } finally {
    autoSaving.value = null;
  }
}

// ── running ──────────────────────────────────────────────────────────────
/** The command waiting on its message, as `repo/id`. */
const asking = ref<string | null>(null);
const runMessage = ref("");
const running = ref<string | null>(null);

async function run(repo: RepoInfo, cmd: RepoCommand): Promise<void> {
  const key = `${repo.name}/${cmd.id}`;
  if (needsMessage(cmd.command) && asking.value !== key) {
    asking.value = key;
    runMessage.value = "";
    return;
  }
  const text = runMessage.value.trim();
  if (needsMessage(cmd.command) && !text) return;
  running.value = key;
  try {
    const session = await runRepoCommand(repo.name, cmd, text);
    asking.value = null;
    viewport.tmuxSession = session;
    viewport.view = "tmux";
    emit("ran");
  } catch (err) {
    toast(message(err));
  } finally {
    running.value = null;
  }
}

// ── editing ──────────────────────────────────────────────────────────────
/** One repo open for editing at a time, on a copy: Cancel puts nothing back. */
const editing = ref<string | null>(null);
const draft = reactive<RepoSettings>({ commands: [], domains: [] });
/** The prompts as typed, every key present: blank is the default. */
const draftPrompts = reactive<Record<RepoPromptKey, string>>({ task: "", autoFix: "", mergeConflict: "" });
/** The worktree hooks as typed, every key present: blank is none. */
const draftHooks = reactive<Record<WorktreeHookKey, string>>({ create: "", remove: "" });
/** What a branch's auto-test runs, as typed: blank is the default. */
const draftTest = ref("");
const newDomain = ref("");
const saving = ref(false);

function startEdit(repo: RepoInfo): void {
  editing.value = repo.name;
  draft.commands = repo.settings.commands.map((c) => ({ ...c }));
  draft.domains = [...repo.settings.domains];
  for (const { key } of REPO_PROMPTS) draftPrompts[key] = repo.settings.prompts?.[key] ?? "";
  for (const { key } of WORKTREE_HOOKS) draftHooks[key] = repo.settings.worktreeHooks?.[key] ?? "";
  draftTest.value = repo.settings.testCommand ?? "";
  newDomain.value = "";
  asking.value = null;
}

function addCommand(): void {
  draft.commands.push({ id: newCommandId(), label: "", command: "" });
}

function move(i: number, by: number): void {
  const j = i + by;
  if (j < 0 || j >= draft.commands.length) return;
  const [c] = draft.commands.splice(i, 1);
  draft.commands.splice(j, 0, c!);
}

/** What a blank prompt means: the default it falls back to. */
function promptDefault(key: RepoPromptKey): string {
  if (key === "task") return taskPromptTemplate.value;
  return key === "autoFix" ? DEFAULT_AUTO_FIX_PROMPT : AUTO_MERGE_CONFLICT_PROMPT;
}

/** The labels of the prompts a repo has its own of, for the card at rest. */
const ownPrompts = (repo: RepoInfo): string =>
  REPO_PROMPTS.filter((p) => repo.settings.prompts?.[p.key])
    .map((p) => p.label)
    .join(", ");

/** The labels of the worktree hooks a repo has, for the card at rest. */
const ownHooks = (repo: RepoInfo): string =>
  WORKTREE_HOOKS.filter((h) => repo.settings.worktreeHooks?.[h.key])
    .map((h) => h.label.toLowerCase())
    .join(", ");

function addDomain(): void {
  const domain = normalizeDomain(newDomain.value);
  if (domain && !draft.domains.includes(domain)) draft.domains.push(domain);
  newDomain.value = "";
}

/** With `only`, the card is always in edit mode: opened as the repo arrives. */
watch(
  () => (props.only ? shown.value[0] : undefined),
  (repo) => {
    if (repo && editing.value !== repo.name) startEdit(repo);
  },
  { immediate: true },
);

/** Out of edit mode — which, with `only`, is out of the modal. */
function stopEdit(): void {
  editing.value = null;
  if (props.only) emit("close");
}

async function save(repo: RepoInfo): Promise<void> {
  // A domain typed but not yet added is meant to be kept.
  if (newDomain.value.trim()) addDomain();
  saving.value = true;
  try {
    await saveRepoSettings(repo.name, {
      // A row left entirely blank is one added and not wanted, not an error.
      commands: draft.commands.filter((c) => c.label.trim() || c.command.trim()),
      domains: draft.domains,
      prompts: { ...draftPrompts },
      worktreeHooks: { ...draftHooks },
      testCommand: draftTest.value,
    });
    // The page fills a task's brief itself, so it needs the repo's template now.
    void loadTaskPromptTemplate();
    stopEdit();
  } catch (err) {
    toast(message(err));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <section class="repo-view" :class="only ? 'repo-view-one' : 'money'" :aria-label="only ? `${only} settings` : 'Repos'">
    <div class="money-inner">
      <div v-if="!only" class="repo-head">
        <input
          v-model="query"
          class="shop-find-input"
          placeholder="Find a repo…"
          aria-label="Find a repo"
          autocomplete="off"
        />
        <button class="btn ghost" type="button" title="Read the list again" @click="load">Refresh</button>
        <button
          class="btn"
          type="button"
          title="An empty repo on the git server, by its newrepo command"
          :disabled="newName !== null"
          @click="newName = ''"
        >+ New repo</button>
      </div>
      <form v-if="!only && newName !== null" class="repo-ask" @submit.prevent="create" @keydown.esc="newName = null">
        <input
          v-model="newName"
          placeholder="Repo name"
          aria-label="New repo name"
          autocomplete="off"
          spellcheck="false"
          maxlength="100"
          v-focus
        />
        <button class="btn primary" type="submit" :disabled="!newNameOk || creating">{{ creating ? "Making…" : "Make repo" }}</button>
        <button class="btn ghost" type="button" @click="newName = null">Cancel</button>
      </form>

      <div v-if="!loaded" class="empty">…</div>
      <div v-else-if="listError" class="empty">{{ listError }}</div>
      <div v-else-if="!repos.length" class="empty">No repos in /home/git/repos.</div>
      <div v-else-if="!only && !query.trim() && !shown.length" class="empty">No repos in {{ teamFolders }}.</div>
      <div v-else-if="!shown.length" class="empty">{{ only ? `No repo ${only} here.` : "No repo matches." }}</div>

      <article v-for="repo in shown" :key="repo.name" class="money-card repo-card" :class="{ editing: editing === repo.name }">
        <header class="repo-card-head">
          <h2>{{ repo.name }}</h2>
          <span
            v-if="repo.claudeTokens"
            class="repo-tokens"
            :title="`${repo.claudeTokens.toLocaleString()} output tokens Claude has written in ${repo.name}: every conversation still on disk, in the checkout or any worktree, removed or not`"
          >{{ formatTokens(repo.claudeTokens) }} tok</span>
          <span style="flex: 1"></span>
          <button v-if="!only && editing !== repo.name" class="btn ghost" type="button" @click="startEdit(repo)">Edit</button>
        </header>
        <div class="repo-autos">
          <label v-for="auto in AUTOS" :key="auto.key" class="repo-auto">
            <input
              type="checkbox"
              :checked="autoOn(repo, auto)"
              :disabled="autoSaving === `${repo.name}/${auto.key}`"
              :aria-describedby="`repo-auto-${repo.name}-${auto.key}`"
              @change="toggleAuto(repo, auto, $event)"
            />
            <span class="repo-auto-label">{{ auto.label }}</span>
            <span :id="`repo-auto-${repo.name}-${auto.key}`" class="repo-auto-blurb">{{ autoOn(repo, auto) ? auto.on : auto.off }}</span>
          </label>
          <label class="repo-auto">
            <input
              type="checkbox"
              :checked="fixOn(repo)"
              :disabled="autoSaving === `${repo.name}/autoFix`"
              :aria-describedby="`repo-auto-${repo.name}-autoFix`"
              @change="changeFix(repo, 'autoFix', () => setAutoFix(repo.name, ($event.target as HTMLInputElement).checked), $event)"
            />
            <span class="repo-auto-label">Auto fix</span>
            <span :id="`repo-auto-${repo.name}-autoFix`" class="repo-auto-blurb">{{ fixOn(repo) ? FIX_ON : FIX_OFF }}</span>
          </label>
          <div class="repo-auto repo-auto-mode">
            <StartModeSelect
              :model-value="startLimitOf(repo.settings)"
              :titles="START_LIMIT_TITLES"
              :without="['manual']"
              label="The repo's start limit"
              :disabled="autoSaving === `${repo.name}/startLimit`"
              @update:model-value="(m) => changeFix(repo, 'startLimit', () => setStartLimit(repo.name, m))"
            />
            <span class="repo-auto-label">Start limit</span>
            <span class="repo-auto-blurb">{{ limitBlurb(startLimitOf(repo.settings)) }}</span>
          </div>
          <label class="repo-auto repo-auto-mode">
            <input
              class="repo-max-claudes"
              type="number"
              inputmode="numeric"
              min="1"
              :max="REPO_MAX_CLAUDES_MAX"
              step="1"
              placeholder="Any"
              :value="maxClaudesOf(repo.settings) ?? ''"
              :disabled="autoSaving === `${repo.name}/maxClaudes`"
              :aria-describedby="`repo-auto-${repo.name}-maxClaudes`"
              @change="changeMaxClaudes(repo, $event)"
              @keydown.enter="($event.target as HTMLInputElement).blur()"
            />
            <span class="repo-auto-label">Max Claudes</span>
            <span :id="`repo-auto-${repo.name}-maxClaudes`" class="repo-auto-blurb">{{ maxClaudesBlurb(maxClaudesOf(repo.settings)) }}</span>
          </label>
        </div>
        <RepoBranches :repo="repo" />
        <FeedbackKeys :repo="repo.name" :teams="repo.teams" />

        <template v-if="editing !== repo.name">
          <div class="repo-domains">
            <a v-for="d in repo.settings.domains" :key="d" class="repo-domain" :href="href(d)" target="_blank" rel="noopener">{{ d }}</a>
            <span v-if="!repo.settings.domains.length" class="money-hint">No domains.</span>
          </div>
          <div class="repo-commands">
            <button
              v-for="cmd in repo.settings.commands"
              :key="cmd.id"
              class="btn"
              :class="{ primary: asking === `${repo.name}/${cmd.id}` }"
              type="button"
              :disabled="!cmd.command || running !== null"
              :title="cmd.command || 'No command yet — Edit to set one'"
              @click="run(repo, cmd)"
            >{{ cmd.label }}{{ needsMessage(cmd.command) ? "…" : "" }}</button>
            <span v-if="!repo.settings.commands.length" class="money-hint">No commands.</span>
          </div>
          <p v-if="ownPrompts(repo)" class="money-hint">Own prompts: {{ ownPrompts(repo) }}.</p>
          <p v-if="ownHooks(repo)" class="money-hint">Worktree hooks: {{ ownHooks(repo) }}.</p>
          <template v-for="cmd in repo.settings.commands" :key="cmd.id">
            <form
              v-if="asking === `${repo.name}/${cmd.id}`"
              class="repo-ask"
              @submit.prevent="run(repo, cmd)"
              @keydown.esc="asking = null"
            >
              <input
                v-model="runMessage"
                :maxlength="RUN_MESSAGE_MAX"
                :placeholder="`${cmd.label} message`"
                :aria-label="`${cmd.label} message`"
                autocomplete="off"
                v-focus
              />
              <button class="btn primary" type="submit" :disabled="!runMessage.trim() || running !== null">Run {{ cmd.label }}</button>
              <button class="btn ghost" type="button" @click="asking = null">Cancel</button>
            </form>
          </template>
        </template>

        <form v-else class="repo-edit" @submit.prevent="save(repo)" @keydown.esc.stop="stopEdit">
          <h3>Commands</h3>
          <div v-for="(cmd, i) in draft.commands" :key="cmd.id" class="repo-cmd-row">
            <input v-model="cmd.label" class="repo-cmd-label" :maxlength="REPO_COMMAND_LABEL_MAX" placeholder="Name" aria-label="Command name" />
            <input
              v-model="cmd.command"
              class="repo-cmd-line"
              :maxlength="REPO_COMMAND_MAX"
              placeholder="bash command, run in the checkout"
              aria-label="Command line"
              spellcheck="false"
              autocomplete="off"
            />
            <button class="btn ghost" type="button" title="Move up" :disabled="i === 0" @click="move(i, -1)">↑</button>
            <button class="btn ghost" type="button" title="Move down" :disabled="i === draft.commands.length - 1" @click="move(i, 1)">↓</button>
            <button class="btn ghost danger" type="button" title="Remove" @click="draft.commands.splice(i, 1)">✕</button>
          </div>
          <div>
            <button class="btn" type="button" @click="addCommand">+ Command</button>
          </div>
          <p class="money-hint">
            Each runs with bash in the claude user's checkout, in a tmux session of its own.
            A command that uses <code>$MESSAGE</code> asks for one before it runs.
          </p>

          <h3>Domains</h3>
          <div class="repo-domains">
            <span v-for="(d, i) in draft.domains" :key="d" class="repo-domain editing">
              {{ d }}
              <button type="button" :title="`Remove ${d}`" @click="draft.domains.splice(i, 1)">✕</button>
            </span>
          </div>
          <div class="repo-ask">
            <input
              v-model="newDomain"
              placeholder="example.com"
              aria-label="Add a domain"
              autocomplete="off"
              spellcheck="false"
              @keydown.enter.prevent="addDomain"
            />
            <button class="btn" type="button" :disabled="!newDomain.trim()" @click="addDomain">Add</button>
          </div>

          <h3>Prompts</h3>
          <label v-for="p in REPO_PROMPTS" :key="p.key" class="repo-prompt">
            <span class="repo-prompt-head">
              {{ p.label }}
              <button
                type="button"
                class="btn ghost"
                title="Put the default text in the box, to edit from"
                :disabled="draftPrompts[p.key] === promptDefault(p.key)"
                @click.prevent="draftPrompts[p.key] = promptDefault(p.key)"
              >Set to default</button>
            </span>
            <textarea
              v-model="draftPrompts[p.key]"
              :maxlength="REPO_PROMPT_MAX"
              :placeholder="promptDefault(p.key)"
              :aria-label="`${p.label} prompt`"
              rows="4"
              spellcheck="false"
            ></textarea>
            <small class="money-hint">{{ p.hint }}</small>
          </label>

          <h3>Worktree hooks</h3>
          <label v-for="h in WORKTREE_HOOKS" :key="h.key" class="repo-prompt">
            <span>{{ h.label }}</span>
            <textarea
              v-model="draftHooks[h.key]"
              :maxlength="REPO_COMMAND_MAX"
              :placeholder="h.placeholder"
              :aria-label="`${h.label} worktree hook`"
              rows="3"
              spellcheck="false"
            ></textarea>
            <small class="money-hint">{{ h.hint }}</small>
          </label>

          <h3>Tests</h3>
          <label class="repo-prompt">
            <span>Test command</span>
            <input
              v-model="draftTest"
              :maxlength="REPO_COMMAND_MAX"
              :placeholder="DEFAULT_TEST_COMMAND"
              aria-label="Test command"
              spellcheck="false"
              autocomplete="off"
            />
            <small class="money-hint">
              What a branch's "test" box runs on each new tip, in a throwaway tree after the create hook above. Blank runs
              <code>{{ DEFAULT_TEST_COMMAND }}</code>.
            </small>
          </label>

          <div class="repo-actions">
            <button class="btn primary" type="submit" :disabled="saving">Save</button>
            <button class="btn ghost" type="button" @click="stopEdit">Cancel</button>
          </div>
        </form>
      </article>
    </div>
  </section>
</template>
